import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { auth, authPersistenceReady, db } from './firebase';
import { portfolioService } from './PortfolioService';
import { sharedAccessService, SharedAccountInfo } from './SharedAccessService';
import { ipTrackingService } from './IPTrackingService';
import { subscriptionService } from './subscriptionService';
import { getApiBaseUrl } from '../utils/api';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { collection, query, where, onSnapshot, updateDoc, doc, Timestamp } from 'firebase/firestore';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  initializeUserAccount: (user: User) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  sendEmailVerification: () => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  isAuthorized: boolean;
  accessMode: 'owner' | 'shared';
  sharedAccessLevel: 'read_only' | 'normal_user' | null;
  sharedAccounts: SharedAccountInfo[];
  currentAccountId: string | null;
  switchAccount: (accountId: string | null) => Promise<void>;
  isReadOnly: boolean;
  syncForumSSO: () => Promise<void>;
  countryCode: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const API_BASE_URL = getApiBaseUrl();

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const isTestModeRef = useRef(false);
  // Shared access state
  const [accessMode, setAccessMode] = useState<'owner' | 'shared'>('owner');
  const [sharedAccessLevel, setSharedAccessLevel] = useState<'read_only' | 'normal_user' | null>(null);
  const [sharedAccounts, setSharedAccounts] = useState<SharedAccountInfo[]>([]);
  const [currentAccountId, setCurrentAccountId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'user' | 'admin' | 'superadmin' | 'read_only' | null>(null);
  const [countryCode, setCountryCode] = useState<string | null>(null);

  // Session idle timeout management
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

  // Reset idle timer on user activity
  const resetIdleTimer = useRef(() => {
    if (!user) return; // Don't run timer when logged out

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(() => {
      console.warn('⏱\ufe0f Session timeout: User has been idle for 30 minutes');
      logout().then(() => {
        // Optionally show a toast notification
        console.log('🔒 Automatically logged out due to inactivity');
      }).catch(err => {
        console.error('Error during auto-logout:', err);
      });
    }, IDLE_TIMEOUT_MS);
  }).current;

  // Set up activity listeners for idle timeout
  useEffect(() => {
    if (!user) return; // Only monitor when logged in

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

    events.forEach(event => {
      document.addEventListener(event, resetIdleTimer);
    });

    // Start the initial timer
    resetIdleTimer();

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, resetIdleTimer);
      });
    };
  }, [user]); // Re-run when user changes (login/logout)

  const checkAndSetAuthStatus = async (authUser: User) => {
    if (!authUser.email) return;

    try {
      console.log('Checking authorization for:', authUser.email);

      // Implement a small retry logic for new users whose documents might still be propagating
      let authStatus = null;
      let userAccountInfo = null;
      let retries = 3;

      while (retries > 0) {
        try {
          authStatus = await portfolioService.checkUserAuthorization(authUser.email);
          userAccountInfo = await portfolioService.getUserAccount(authUser.email);

          if (authStatus && authStatus.authorized && userAccountInfo) {
            break;
          }
        } catch (e) {
          console.warn(`Authorization check attempt failed (${retries} retries left):`, e);
        }

        console.log(`Waiting for user documents to initialize... (${retries} retries left)`);
        await new Promise(resolve => setTimeout(resolve, 800));
        retries--;
      }

      console.log('Authorization status:', authStatus);

      // If authorization check failed (authStatus is null), it means we couldn't even verify existence after retries
      if (!authStatus) {
        console.error('❌ Failed to verify user authorization after retries');
        // This is a more severe error than not authorized, maybe sign out?
        // For now, let's treat it as NOT authorized so they get redirected to register
        setIsAuthorized(false);
        setLoading(false);
        return;
      }

      setIsAuthorized(authStatus.authorized);
      setIsAdmin(authStatus.isAdmin);
      // @ts-ignore - Assuming detailed response includes role
      if (authStatus.role) setUserRole(authStatus.role);
      if (authStatus.countryCode) setCountryCode(authStatus.countryCode);

      // If not authorized but we have a user, don't sign out yet
      // This allows the UI to handle the redirect to registration
      if (!authStatus.authorized) {
        console.warn('⚠️ User is authenticated but NOT authorized in database');
        setIsAuthorized(false);
        setLoading(false);
        return;
      }

      // Fetch shared accounts first
      let shared: SharedAccountInfo[] = [];
      try {
        shared = await sharedAccessService.getMySharedAccess();
        setSharedAccounts(shared);
      } catch (err) {
        console.error('Error loading shared access:', err);
      }

      // Get the user's assigned account ID
      if (userAccountInfo && userAccountInfo.accountId) {
        setCurrentAccountId(userAccountInfo.accountId);
        if (userAccountInfo.role) {
          // @ts-ignore
          setUserRole(userAccountInfo.role);
        }
      } else if (shared.length > 0) {
        // Auto-switch to first shared account if no personal account
        console.log('No personal account found, switching to shared account:', shared[0].accountId);
        setAccessMode('shared');
        setSharedAccessLevel(shared[0].accessLevel);
        setCurrentAccountId(shared[0].accountId);
      } else {
        // Fallback to UID if no explicit assignment and no shared accounts
        setCurrentAccountId(authUser.uid);
      }
    } catch (error) {
      console.error('Error checking authorization:', error);
      setIsAuthorized(false);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    const startTime = Date.now();
    console.log('Auth listener initializing...');

    // Use ref to store unsubscribe function for cleanup
    let unsubscribe: (() => void) | null = null;
    let authTimeoutId: ReturnType<typeof setTimeout> | null = null;

    // CRITICAL: Wait for persistence to be configured BEFORE setting up auth listener
    // The timeout should only start AFTER persistence is ready
    authPersistenceReady
      .then(() => {
        const persistenceTime = Date.now() - startTime;
        console.log(`✅ Persistence ready after ${persistenceTime}ms, now setting up auth listener`);

        // NOW start the timeout - only after persistence is configured
        // This gives auth 5 more seconds AFTER persistence is ready
        authTimeoutId = setTimeout(() => {
          const elapsed = Date.now() - startTime;
          console.warn(`⚠️ Auth initialization timeout after ${elapsed}ms - proceeding without session`);
          console.warn('Note: Auth listener did not fire within timeout period.');
          // Track this timeout event in analytics
          import('./analytics').then(({ trackAuthStorageIssue }) => {
            trackAuthStorageIssue('auth_timeout', { elapsedMs: elapsed });
          }).catch(() => { });
          setLoading(false);
        }, 5000); // 5 seconds AFTER persistence is ready

        // Handle redirect result (from signInWithRedirect) - non-blocking
        getRedirectResult(auth)
          .then((result) => {
            if (result && result.user) {
              console.log('Redirect sign-in successful:', result.user.email);
              ipTrackingService.recordIP('login').catch(e => console.error('Failed to record IP:', e));
            }
          })
          .catch((error) => {
            if (error.code !== 'auth/popup-closed-by-user') {
              console.warn('Redirect result check:', error.code || error.message);
            }
          });

        // NOW set up auth listener - AFTER persistence is configured
        // Subscription listener cleanup function
        let unsubscribeSubscriptions: (() => void) | null = null;

        // NOW set up auth listener - AFTER persistence is configured
        unsubscribe = onAuthStateChanged(auth, async (authUser: User | null) => {
          // Cleanup previous subscription listener if it exists
          if (unsubscribeSubscriptions) {
            unsubscribeSubscriptions();
            unsubscribeSubscriptions = null;
          }

          // Auth fired successfully, clear the timeout
          if (authTimeoutId) {
            clearTimeout(authTimeoutId);
            authTimeoutId = null;
          }
          console.log('Auth state changed:', authUser ? `User: ${authUser.email}` : 'No user (logged out)');
          setUser(authUser);

          if (authUser && authUser.email) {
            // Background SSO initialization for the forum
            // We use a small flag in localStorage to avoid re-fetching the SSO token too often
            const lastSSOSync = localStorage.getItem('last_forum_sso_sync');
            const now = Date.now();
            const ONE_HOUR = 60 * 60 * 1000;

            if (!lastSSOSync || (now - parseInt(lastSSOSync) > ONE_HOUR)) {
              console.log('🔄 Init background forum SSO sync...');
              (async () => {
                try {
                  const token = await authUser.getIdToken();
                  const response = await fetch('/api/forumSSO', {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  if (response.ok) {
                    const data = await response.json();
                    if (data.token && data.cookieName) {
                      // Set cookie on root domain so it propagates to /forum subpath
                      const getCookieDomain = () => {
                        const hostname = window.location.hostname;
                        if (hostname.endsWith('canvasestate.com.au')) return 'domain=canvasestate.com.au;';
                        if (hostname.endsWith('canvasestate.com')) return 'domain=canvasestate.com;';
                        return '';
                      };

                      const domainAttribute = getCookieDomain();
                      document.cookie = `${data.cookieName}=${data.token}; path=/; ${domainAttribute} secure; samesite=lax`;
                      localStorage.setItem('last_forum_sso_sync', now.toString());
                      console.log(`✓ Background Forum SSO cookie synced (${window.location.hostname})`);
                    }
                  }
                } catch (e) {
                  console.warn('Background forum SSO sync failed:', e);
                }
              })();
            }

            try {
              await checkAndSetAuthStatus(authUser);

              // Set up Stripe subscription listener
              // Firebase Extension syncs subscriptions to customers/{uid}/subscriptions
              const subscriptionsQuery = query(
                collection(db, 'customers', authUser.uid, 'subscriptions'),
                where('status', 'in', ['active', 'trialing'])
              );

              unsubscribeSubscriptions = onSnapshot(
                subscriptionsQuery,
                async (snapshot) => {
                  if (!snapshot.empty) {
                    // User has an active Stripe subscription
                    const subscriptionDoc = snapshot.docs[0];
                    const stripeSubscription = subscriptionDoc.data() as any;

                    console.log('Stripe subscription updated:', stripeSubscription);

                    // Extract plan ID from price metadata or product
                    const planId = stripeSubscription.items?.[0]?.price?.product?.metadata?.planId ||
                      stripeSubscription.metadata?.planId ||
                      'base';

                    // Sync subscription status to authorized_users
                    try {
                      await updateDoc(doc(db, 'authorized_users', authUser.email!.toLowerCase()), {
                        planId: planId,
                        status: stripeSubscription.status === 'trialing' ? 'trial' : 'active',
                        isTrialActive: stripeSubscription.status === 'trialing',
                        stripeCustomerId: stripeSubscription.metadata?.firebaseUID || authUser.uid,
                        stripeSubscriptionId: subscriptionDoc.id,
                        subscriptionStartedAt: stripeSubscription.created
                          ? Timestamp.fromMillis(stripeSubscription.created * 1000)
                          : Timestamp.now(),
                        currentPeriodStart: stripeSubscription.current_period_start
                          ? Timestamp.fromMillis(stripeSubscription.current_period_start * 1000)
                          : undefined,
                        currentPeriodEnd: stripeSubscription.current_period_end
                          ? Timestamp.fromMillis(stripeSubscription.current_period_end * 1000)
                          : undefined,
                        updatedAt: Timestamp.now()
                      });
                      console.log('✓ Subscription synced to authorized_users with plan:', planId);
                    } catch (error) {
                      console.error('Error syncing subscription:', error);
                    }
                  }
                },
                (error) => {
                  console.error('Error listening to subscriptions:', error);
                }
              );

            } catch (error) {
              console.error('Error in auth observer:', error);
            } finally {
              // Only set loading to false after authorization check completes
              setLoading(false);
            }
          } else {
            console.log('No user authenticated, setting authorized=false');
            setIsAuthorized(false);
            setIsAdmin(false);
            setLoading(false);
          }
        });
      })
      .catch((err) => {
        console.warn('Persistence setup had issues, continuing anyway:', err);
        // Still set up auth listener even if persistence fails
        setLoading(false);
      });

    return () => {
      if (authTimeoutId) {
        clearTimeout(authTimeoutId);
      }
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const switchAccount = async (accountId: string | null) => {
    if (!user) return;

    if (!accountId) {
      // Switch back to own account
      setAccessMode('owner');
      setSharedAccessLevel(null);
      const userAccountInfo = await portfolioService.getUserAccount(user.email!);
      setCurrentAccountId(userAccountInfo?.accountId || user.uid);
      if (userAccountInfo?.role) {
        // @ts-ignore
        setUserRole(userAccountInfo.role);
      }
    } else {
      // Find the shared account
      const targetAccount = sharedAccounts.find(acc => acc.accountId === accountId);
      if (targetAccount) {
        setAccessMode('shared');
        setSharedAccessLevel(targetAccount.accessLevel);
        setCurrentAccountId(accountId);
      } else {
        console.error('Cannot switch to unknown account:', accountId);
      }
    }
  };

  const isReadOnly = userRole === 'read_only' || (accessMode === 'shared' && sharedAccessLevel === 'read_only');

  const signIn = async (email: string, password: string) => {
    console.log('signIn called with:', email);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Free tier: no trial expiry check needed
    } catch (error: any) {
      // Auto-create test users if they don't exist (Dev/Test helper)
      if (error.code === 'auth/user-not-found' && (email === 'test@test.com' || email === 'user@test.com')) {
        console.log('Test user not found, creating account...');
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        // Log original error and throw it for LoginForm to catch
        console.warn('Sign-in error:', error.code, error.message);
        throw error;
      }
    }
    // Record IP address after successful login
    try {
      await ipTrackingService.recordIP('login');
    } catch (e) {
      console.error('Failed to record IP:', e);
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);

      // Free tier: no trial expiry check needed

      // Ensure newly authenticated Google users are initialized immediately
      // so they do not get stuck as authenticated-but-unauthorized.
      if (result.user.email) {
        const authStatus = await portfolioService.checkUserAuthorization(result.user.email);
        if (!authStatus.authorized) {
          await initializeUserAccount(result.user);
          return;
        }
      }

      // Record IP address after successful login
      try {
        await ipTrackingService.recordIP('login');
      } catch (e) {
        console.error('Failed to record IP:', e);
      }
    } catch (error: any) {
      // If popup is blocked, fall back to redirect method
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        console.log('Popup blocked, falling back to redirect...');
        await signInWithRedirect(auth, provider);
      } else {
        throw error;
      }
    }
  };

  const initializeUserAccount = async (newUser: User) => {
    if (!newUser.email) return;

    let initializationSucceeded = false;

    // Initialize via backend Admin SDK endpoint (works even when client Firestore rules
    // block direct writes for new users).
    try {
      const registrationEndpoints = ['/api/selfRegister', `${API_BASE_URL}/selfRegister`];
      let lastApiError: Error | null = null;
      const maxAttempts = 3;

      for (let attempt = 1; attempt <= maxAttempts && !initializationSucceeded; attempt++) {
        const forceRefresh = attempt > 1;
        const idToken = await newUser.getIdToken(forceRefresh);

        for (const endpoint of registrationEndpoints) {
          try {
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              initializationSucceeded = true;
              console.log(`✓ User self-registration initialized via ${endpoint} for`, newUser.email);
              break;
            }

            const errorData = await response.json().catch(() => ({ error: 'Failed to initialize account' }));
            lastApiError = new Error(errorData.error || `Failed to initialize account (${response.status})`);
            console.warn(`selfRegister endpoint failed (${endpoint}) [attempt ${attempt}/${maxAttempts}]:`, lastApiError.message);
          } catch (endpointError) {
            lastApiError = endpointError instanceof Error ? endpointError : new Error('Failed to initialize account');
            console.warn(`selfRegister network error (${endpoint}) [attempt ${attempt}/${maxAttempts}]:`, lastApiError.message);
          }
        }

        if (!initializationSucceeded && attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, attempt * 400));
        }
      }

      if (!initializationSucceeded && lastApiError) {
        throw lastApiError;
      }

    } catch (error) {
      console.error('Failed to initialize account via API:', error);

      // If auth/assignment already completed in parallel (race with listener/trigger),
      // treat as success and continue instead of hard-failing registration UX.
      try {
        const authStatus = await portfolioService.checkUserAuthorization(newUser.email);
        const userAccount = await portfolioService.getUserAccount(newUser.email);
        if (authStatus.authorized && userAccount?.accountId) {
          initializationSucceeded = true;
          console.log('✓ Account already initialized; continuing registration flow for', newUser.email);
        }
      } catch (statusError) {
        console.warn('Authorization/account re-check failed after API error:', statusError);
      }

      // Fallback for local/dev environments where API may not be available.
      if (!initializationSucceeded) {
        try {
          await subscriptionService.initializeTrialSubscription(newUser.email, newUser.uid);
          const accountId = `acc-${newUser.uid}`;
          await portfolioService.createAccount(accountId, newUser.email);
          initializationSucceeded = true;
          console.log('✓ Fallback client-side account initialization completed:', accountId);
        } catch (fallbackError) {
          console.error('Fallback account initialization failed:', fallbackError);
        }
      }
    }

    if (!initializationSucceeded) {
      throw new Error('Unable to complete user account initialization');
    }

    // Record IP address after successful registration
    try {
      await ipTrackingService.recordIP('register');
    } catch (e) {
      console.error('Failed to record IP:', e);
    }

    // CRITICAL: Manually refresh authorization status after documents are created
    // this avoids the race condition where the onAuthStateChanged listener 
    // fires before the Firestore documents are ready.
    try {
      console.log('Performing manual authorization refresh for new user');
      // Increase delay to 1500ms to ensure Firestore writes and triggers have stabilized
      await new Promise(resolve => setTimeout(resolve, 1500));
      await checkAndSetAuthStatus(newUser);
    } catch (error) {
      console.error('Failed to refresh authorization status after initialization:', error);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;
      await initializeUserAccount(newUser);
    } catch (error: any) {
      // Dev fallback: some environments enforce App Check on Auth endpoints and can
      // reject createUserWithEmailAndPassword with invalid-token errors.
      const errorCode = String(error?.code || '');
      const errorMessage = String(error?.message || '');
      const isAppCheckSignupError =
        errorCode === 'auth/firebase-app-check-token-is-invalid' ||
        errorMessage.includes('firebase-app-check-token-is-invalid') ||
        errorMessage.includes('app-check-token-is-invalid');

      if (isAppCheckSignupError) {
        const endpoints = ['/api/registerEmailUser', `${API_BASE_URL}/registerEmailUser`];
        let fallbackError: Error | null = null;

        for (const endpoint of endpoints) {
          try {
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email, password })
            });

            if (!response.ok) {
              const data = await response.json().catch(() => ({ error: 'Email registration fallback failed' }));
              throw new Error(data.error || `Email registration fallback failed (${response.status})`);
            }

            await response.json().catch(() => ({}));
            await signInWithEmailAndPassword(auth, email, password);
            if (auth.currentUser) {
              await checkAndSetAuthStatus(auth.currentUser);
            }
            return;
          } catch (fallbackAttemptError) {
            fallbackError = fallbackAttemptError instanceof Error
              ? fallbackAttemptError
              : new Error('Email registration fallback failed');
            console.warn(`Email registration fallback failed via ${endpoint}:`, fallbackError.message);
          }
        }

        throw fallbackError || error;
      }

      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  const sendEmailVerificationFn = async () => {
    if (auth.currentUser) {
      try {
        await sendEmailVerification(auth.currentUser);
      } catch (error) {
        console.error('Email verification error:', error);
        throw error;
      }
    } else {
      throw new Error('No user is currently signed in');
    }
  };

  const logout = async () => {
    if (isTestModeRef.current) {
      console.log('Logging out from test mode');
      isTestModeRef.current = false;
      localStorage.removeItem('test_user_session');
      setUser(null);
      setIsAdmin(false);
      setIsAuthorized(false);
      setAccessMode('owner');
      setSharedAccessLevel(null);
      setSharedAccounts([]);
      setCurrentAccountId(null);
      setUserRole(null);
      window.location.href = '/';
      return; // Changed from Promise.resolve() to return
    }
    await signOut(auth);
    // Clear forum SSO cookie
    const getCookieDomain = () => {
      const hostname = window.location.hostname;
      if (hostname.endsWith('canvasestate.com.au')) return 'domain=canvasestate.com.au;';
      if (hostname.endsWith('canvasestate.com')) return 'domain=canvasestate.com;';
      return '';
    };

    const domainAttribute = getCookieDomain();
    document.cookie = `__session=; path=/; ${domainAttribute} expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `canvas_sso=; path=/; ${domainAttribute} expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    localStorage.removeItem('last_forum_sso_sync');

    window.location.href = '/';
  };

  const syncForumSSO = async () => {
    // Current user reference can be unstable during render, use auth.currentUser
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    try {
      console.log('🔄 Manual forum SSO sync started...');
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/forumSSO', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.token && data.cookieName) {
          // Sync cookie to domain for all subpaths
          // Only use domain attribute if we are on the production domain
          const getCookieDomain = () => {
            const hostname = window.location.hostname;
            if (hostname.endsWith('canvasestate.com.au')) return 'domain=canvasestate.com.au;';
            if (hostname.endsWith('canvasestate.com')) return 'domain=canvasestate.com;';
            return '';
          };

          const domainAttribute = getCookieDomain();
          document.cookie = `${data.cookieName}=${data.token}; path=/; ${domainAttribute} secure; samesite=lax`;
          localStorage.setItem('last_forum_sso_sync', Date.now().toString());
          console.log(`✓ Forum SSO cookie synced (${window.location.hostname})`);
        }
      }
    } catch (e) {
      console.warn('Manual forum SSO sync failed:', e);
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signInWithGoogle,
    signUp,
    initializeUserAccount,
    resetPassword,
    sendEmailVerification: sendEmailVerificationFn,
    logout,
    isAdmin,
    isAuthorized,
    accessMode,
    sharedAccessLevel,
    sharedAccounts,
    currentAccountId,
    switchAccount,
    isReadOnly,
    syncForumSSO,
    countryCode
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}